// processHero.tsx
// Extracts and processes hero sections (e.g., <main> or hero section) from HTML, applies overlay/blur, and returns updated HTML.

import { AiOutlineVerticalAlignTop } from "react-icons/ai";

export function processHero(html: string): string {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find the first element with a background-image style and class containing 'bg-cover'
        let heroElem: HTMLElement | null = null;
        const candidates = Array.from(doc.querySelectorAll('[style*="background-image"]')) as HTMLElement[];
        for (const el of candidates) {
            if (el.className && el.className.includes('bg-cover')) {
                heroElem = el;
                break;
            }
        }
        // fallback: first <main> or section with class containing 'hero'
        if (!heroElem) {
            heroElem = doc.querySelector('main') || doc.querySelector('[class*="hero"]');
        }
        if (!heroElem) return html;

        // Check for background image in style or class
        let bgUrl: string | null = null;
        // Check inline style
        const style = heroElem.getAttribute('style') || '';
        const bgMatch = style.match(/background-image:\s*url\(([^)]+)\)/);
        if (bgMatch) {
            bgUrl = bgMatch[1];
        } else {
            // Check class for bg-[url(...)]
            const className = heroElem.className || '';
            const classMatch = className.match(/bg-\[url\(([^)]+)\)\]/);
            if (classMatch) {
                bgUrl = classMatch[1];
                // Add inline style if not present
                heroElem.setAttribute('style', (style ? style + '; ' : '') + `background-image: url(${bgUrl})`);
            }
        }
        if (!bgUrl) return html;

        // Remove blur from main hero element, set up stacking context
        const existingStyle = heroElem.getAttribute('style') || '';
        heroElem.setAttribute('style', existingStyle + (existingStyle ? '; ' : '') + 'position: relative; overflow: hidden;');

        // Add blurred background image as first child
        const blurBg = doc.createElement('div');
        blurBg.className = 'hero-blur-bg';
        blurBg.style.position = 'absolute';
        blurBg.style.top = '0';
        blurBg.style.left = '0';
        blurBg.style.width = '100%';
        blurBg.style.height = '100%';
        blurBg.style.backgroundImage = `url(${bgUrl})`;
        blurBg.style.backgroundSize = 'cover';
        blurBg.style.backgroundPosition = 'center';
        blurBg.style.backgroundRepeat = 'no-repeat';
        blurBg.style.filter = 'blur(8px)';
        blurBg.style.zIndex = '0';
        blurBg.style.pointerEvents = 'none';
        heroElem.insertBefore(blurBg, heroElem.firstChild);

        // Add a dark gradient overlay as second child
        const overlay = doc.createElement('div');
        overlay.id = 'hero-overlay';
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.background = 'linear-gradient(90deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '1';
        heroElem.insertBefore(overlay, heroElem.children[1] || null);

        // Ensure all other direct children (content) are above overlay
        for (let i = 0; i < heroElem.children.length; i++) {
            const child = heroElem.children[i];
            if (child !== blurBg && child !== overlay) {
                (child as HTMLElement).style.position = 'relative';
                (child as HTMLElement).style.zIndex = '2';
            }
        }

        return doc.body.innerHTML;
    } catch (error) {
        // Debug log removed
        return html;
    }
}
